import React, { useEffect, useRef, useState } from "react";
import { getHeaderClasses } from "../../../ConsolidatedReportsUtilities";
import { getStandardSetTransitionBorder } from "../LearnerScoresTableUtilities";

const HeadersRowLessonName = ({ showStandardInfo, labelSplit, standard }) => {
  const ref = useRef(null);
  const [currentWidth, setCurrentWidth] = useState(0);

  useEffect(() => {
    setCurrentWidth(ref.current ? ref.current.offsetWidth : 0);
  }, [ref.current, showStandardInfo]);

  return (
    <span
      className={`header_row_lesson_name standards_showing_${showStandardInfo}`}
      style={{ display: "flex" }}
    >
      <p
        style={{
          maxWidth: showStandardInfo
            ? `${150 - standard.length * 6}px`
            : "160px",
          overflow: "hidden",
        }}
        ref={ref}
      >
        {labelSplit}
      </p>
      <span>
        {showStandardInfo && currentWidth >= 150 - standard.length * 6
          ? "..."
          : !showStandardInfo && currentWidth >= 160
          ? "..."
          : ""}
      </span>
    </span>
  );
};

/**
 * HeadersRow Creates individual lesson column headers for the table.
 * Each header displays lesson name, optional standard information,
 * and includes click handling for sorting functionality.
 * Learner, Average, and Actions labels will
 * always appear, but the number of columns in between varies.
 * @param {Array} filteredLessons - Array of lesson objects to create headers for
 * @param {Function} hasBorder - Function to determine if column should have border
 * @param {Function} handleColumnHeaderClick - Click handler for column sorting
 * @param {string} sortType - Current sort type for styling active sort column
 * @param {boolean} sortAsc - Whether sort is ascending
 * @param {Object} listOfUnits - Mapping of units to standards
 * @param {boolean} showStandardInfo - Whether to display standard codes
 * @param {boolean} showPreferredStandardInfo - Whether to show preferred standards
 * @param {string} standardSet - Current standard set being used
 * @returns {Array<JSX.Element>} Array of JSX div elements, each representing a lesson column header
 */

const HeadersRow = ({
  filteredLessons,
  hasBorder,
  handleColumnHeaderClick,
  sortType,
  sortAsc,
  listOfUnits,
  showStandardInfo,
  showPreferredStandardInfo,
  standardSet,
}) => {
  return filteredLessons.map((value, index) => {
    let label = value.label;

    let standard = "";
    if (
      value.standard &&
      value.standard !== "N/A" &&
      value.standard !== "N/A api/view"
    ) {
      standard = value.standard;
    } else if (listOfUnits[value.value]?.standard) {
      standard = listOfUnits[value.value].standard;
    } else {
      standard = "N/A";
    }

    if (standard.startsWith("CCSS.MATH.CONTENT.")) {
      standard = standard.replace("CCSS.MATH.CONTENT.", "");
    } else if (standard.startsWith("CCSS.ELA-LITERACY.")) {
      standard = standard.replace("CCSS.ELA-LITERACY.", "");
    } else if (standard.startsWith("CCSS.")) {
      standard = standard.replace("CCSS.", "");
    }

    const standardDescription =
      value.standard_desc &&
      value.standard_desc !== "N/A" &&
      value.standard_desc !== "N/A api/view"
        ? value.standard_desc
        : "No description available";

    const standardSetName =
      value.standardset &&
      value.standardset !== "N/A" &&
      value.standardset !== "N/A api/view"
        ? value.standardset
        : "Unknown";

    const transitionBorder = getStandardSetTransitionBorder(
      filteredLessons,
      index,
      standardSet,
      showPreferredStandardInfo
    );

    let labelSplit =
      label.indexOf("- TUT") !== -1
        ? label.split("- TUT")[0].trim()
        : label.split("- ASM")[0].trim();

    return (
      <div
        key={index}
        className='label_container reports_page_hover_styling'
        style={{
          borderRight: hasBorder(index, filteredLessons.length),
          borderLeft: transitionBorder,
        }}
        onClick={() => {
          handleColumnHeaderClick(filteredLessons, label);
        }}
        data-title={
          showStandardInfo
            ? `Lesson Name: ${labelSplit}
Standard Set: ${standardSetName}
Standard Desc: ${standardDescription}`
            : `Standard: ${standard}
Standard Set: ${standardSetName}
Standard Desc: ${standardDescription}`
        }
      >
        <span className={`data label lessons_${filteredLessons.length}`}>
          {showStandardInfo && (
            <span className='header_row_standards'>{standard} </span>
          )}

          <HeadersRowLessonName
            showStandardInfo={showStandardInfo}
            labelSplit={labelSplit}
            standard={standard}
          />
        </span>
        <span
          className={getHeaderClasses(label, sortType, sortAsc)}
          style={{ textAlign: "center", justifyContent: "center" }}
        ></span>
      </div>
    );
  });
};

export default HeadersRow;
